import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, X } from 'lucide-react';

export interface CartItem {
  id: string;
  name: string;
  price: number;
  image?: string;
  quantity: number;
  slug?: string;
  size?: string;
}

/** Maps a product slug to the payment price id created for that product. */
export const priceIdForSlug = (slug: string) =>
  `${slug.replace(/-/g, '_')}_onetime`;


export const BUNDLE_MIN_ITEMS = 2;
export const BUNDLE_DISCOUNT_PERCENT = 25;

interface CartContextValue {
  items: CartItem[];
  count: number;
  /** Total before the multi-piece discount. */
  subtotal: number;
  /** Amount taken off when 2 or more pieces are in the cart. */
  discount: number;
  /** Final payable total. */
  total: number;
  addItem: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

const STORAGE_KEY = 'heavenmade_cart';

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as CartItem[]) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore */
    }
  }, [items]);

  const value = useMemo<CartContextValue>(() => {
    const count = items.reduce((sum, i) => sum + i.quantity, 0);
    const subtotal = items.reduce((sum, i) => sum + i.quantity * i.price, 0);
    const discount = count >= BUNDLE_MIN_ITEMS
      ? Math.round((subtotal * BUNDLE_DISCOUNT_PERCENT) / 100)
      : 0;
    return {
    items,
    count,
    subtotal,
    discount,
    total: subtotal - discount,
    // Only one of each piece exists, so quantity is always locked to 1.
    addItem: (item) =>
      setItems((prev) =>
        prev.some((i) => i.id === item.id || (item.slug && i.slug === item.slug))
          ? prev
          : [...prev, { ...item, quantity: 1 }],
      ),
    removeItem: (id) => setItems((prev) => prev.filter((i) => i.id !== id)),
    updateQuantity: (id, quantity) =>
      setItems((prev) =>
        quantity <= 0
          ? prev.filter((i) => i.id !== id)
          : prev.map((i) => (i.id === id ? { ...i, quantity: 1 } : i)),
      ),
    clearCart: () => setItems([]),
    isOpen,
    openCart: () => setIsOpen(true),
    closeCart: () => setIsOpen(false),
    };
  }, [items, isOpen]);

  return (
    <CartContext.Provider value={value}>
      {children}
      <CartDrawer />
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};

const typewriter = { fontFamily: "'Special Elite', cursive" } as const;

export const CartButton = () => {
  const { count, openCart } = useCart();
  return (
    <button
      onClick={openCart}
      aria-label="Open shopping cart"
      className="relative text-black hover:text-black/70 transition-colors cursor-pointer"
    >
      <ShoppingCart className="w-6 h-6" strokeWidth={1.5} />
      {count > 0 && (
        <span
          className="absolute -top-2 -right-2 bg-black text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full"
          style={typewriter}
        >
          {count}
        </span>
      )}
    </button>
  );
};

const CartDrawer = () => {
  const { items, isOpen, closeCart, removeItem, total, subtotal, discount, count } = useCart();
  const navigate = useNavigate();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" onClick={closeCart}>
      <div className="absolute inset-0 bg-black/50" />
      <aside
        className="relative bg-white border-l-2 border-black w-full max-w-sm h-full p-6 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <p className="text-black" style={typewriter}>Your Cart</p>
          <button onClick={closeCart} aria-label="Close cart" className="text-black">
            <X className="w-5 h-5" />
          </button>
        </div>

        {items.length === 0 ? (
          <p className="text-black text-sm" style={typewriter}>Your cart is empty.</p>
        ) : (
          <>
            <ul className="space-y-4">
              {items.map((item) => (
                <li key={item.id} className="flex gap-3 items-center border-b border-black/20 pb-3">
                  {item.image && (
                    <img src={item.image} alt={item.name} className="w-16 h-16 object-cover" />
                  )}
                  <div className="flex-1">
                    <p className="text-black text-sm" style={typewriter}>{item.name}</p>
                    <p className="text-black text-xs" style={typewriter}>
                      {item.price} SEK
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-black text-[10px]" style={typewriter}>
                        1 of 1 available
                      </span>
                      <button
                        className="ml-auto text-black text-xs underline"
                        style={typewriter}
                        onClick={() => removeItem(item.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {count < BUNDLE_MIN_ITEMS ? (
              <button
                className="mt-6 w-full border-2 border-black py-3 text-black text-xs"
                style={typewriter}
                onClick={() => {
                  closeCart();
                  navigate('/');
                }}
              >
                Add one more piece — get {BUNDLE_DISCOUNT_PERCENT}% off the whole order
              </button>
            ) : (
              <p className="mt-6 text-black text-xs" style={typewriter}>
                {BUNDLE_DISCOUNT_PERCENT}% multi-piece discount applied
              </p>
            )}

            <div className="mt-4 flex items-center justify-between">
              <span className="text-black text-sm" style={typewriter}>Subtotal</span>
              <span className="text-black text-sm" style={typewriter}>{subtotal} SEK</span>
            </div>
            {discount > 0 && (
              <div className="mt-1 flex items-center justify-between">
                <span className="text-black text-sm" style={typewriter}>Discount</span>
                <span className="text-black text-sm" style={typewriter}>−{discount} SEK</span>
              </div>
            )}
            <div className="mt-1 flex items-center justify-between">
              <span className="text-black text-sm" style={typewriter}>Total</span>
              <span className="text-black text-sm" style={typewriter}>{total} SEK</span>
            </div>
            <p className="text-black text-[10px] mt-1" style={typewriter}>Tax & shipping included</p>
            <button
              className="mt-4 w-full bg-black text-white py-3 hover:bg-black/80 transition-colors"
              style={typewriter}
              onClick={() => {
                closeCart();
                navigate('/checkout');
              }}
            >
              Checkout
            </button>
          </>
        )}
      </aside>
    </div>
  );
};
